import { useState, useEffect, useCallback } from 'react';
import { emailTemplateService, EmailTemplate, CreateEmailTemplateDto, UpdateEmailTemplateDto, QueryEmailTemplateDto, SendTestEmailDto } from '../services/email-template.service';

interface UseEmailTemplatesReturn {
  templates: EmailTemplate[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  filters: QueryEmailTemplateDto;
  setFilters: (filters: QueryEmailTemplateDto) => void;
  refetch: () => void;
  createTemplate: (data: CreateEmailTemplateDto) => Promise<EmailTemplate>;
  updateTemplate: (id: string, data: UpdateEmailTemplateDto) => Promise<EmailTemplate>;
  deleteManyTemplates: (ids: string[]) => Promise<void>;
  cloneTemplate: (id: string) => Promise<EmailTemplate>;
}

export const useEmailTemplates = (initialFilters: QueryEmailTemplateDto = {}): UseEmailTemplatesReturn => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<QueryEmailTemplateDto>({
    page: 1,
    limit: 20,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await emailTemplateService.getAll(filters);
      setTemplates(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch email templates'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const createTemplate = useCallback(async (data: CreateEmailTemplateDto): Promise<EmailTemplate> => {
    const template = await emailTemplateService.create(data);
    await fetchTemplates();
    return template;
  }, [fetchTemplates]);

  const updateTemplate = useCallback(async (id: string, data: UpdateEmailTemplateDto): Promise<EmailTemplate> => {
    const template = await emailTemplateService.update(id, data);
    setTemplates((prev) => prev.map((t) => (t._id === id ? { ...t, ...template } : t)));
    return template;
  }, []);

  const deleteManyTemplates = useCallback(async (ids: string[]): Promise<void> => {
    await emailTemplateService.deleteMany(ids);
    setTemplates((prev) => prev.filter((t) => !ids.includes(t._id)));
    setMeta((prev) => ({ ...prev, total: prev.total - ids.length }));
  }, []);

  const cloneTemplate = useCallback(async (id: string): Promise<EmailTemplate> => {
    const template = await emailTemplateService.clone(id);
    await fetchTemplates();
    return template;
  }, [fetchTemplates]);

  return { templates, loading, error, meta, filters, setFilters, refetch: fetchTemplates, createTemplate, updateTemplate, deleteManyTemplates, cloneTemplate };
};

interface UseEmailTemplateDetailReturn {
  template: EmailTemplate | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  updateTemplate: (data: UpdateEmailTemplateDto) => Promise<EmailTemplate>;
  sendTest: (data: SendTestEmailDto) => Promise<{ message: string }>;
}

export const useEmailTemplateDetail = (id?: string): UseEmailTemplateDetailReturn => {
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplate = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await emailTemplateService.getById(id);
      setTemplate(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch email template'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const updateTemplate = useCallback(async (data: UpdateEmailTemplateDto): Promise<EmailTemplate> => {
    if (!id) throw new Error('No template id');
    const updated = await emailTemplateService.update(id, data);
    setTemplate(updated);
    return updated;
  }, [id]);

  const sendTest = useCallback(async (data: SendTestEmailDto): Promise<{ message: string }> => {
    return emailTemplateService.sendTest(data);
  }, []);

  return { template, loading, error, refetch: fetchTemplate, updateTemplate, sendTest };
};
